// Email-chips input: type an address + Enter / comma / space to add it as a
// chip; Backspace on an empty field removes the last. Validated + de-duped,
// capped at 20 (mirrors the server). Controlled — the parent owns the list.

import { ChangeEvent, KeyboardEvent, MouseEvent, memo, useCallback, useState } from 'react';

import { IconClose } from '../ui/icons';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX = 20;

export const EmailChips = memo(function EmailChips({
  emails,
  onChange,
  placeholder,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');

  const commit = useCallback(
    (raw: string): boolean => {
      const e = raw.trim().toLowerCase();
      if (e && EMAIL_RE.test(e) && !emails.includes(e) && emails.length < MAX) {
        onChange([...emails, e]);
        return true;
      }
      return false;
    },
    [emails, onChange],
  );

  const onInput = useCallback((ev: ChangeEvent<HTMLInputElement>) => setText(ev.target.value), []);
  const onKey = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter' || ev.key === ',' || ev.key === ' ') {
        if (text.trim()) {
          ev.preventDefault();
          if (commit(text)) setText('');
        }
      } else if (ev.key === 'Backspace' && text === '' && emails.length) {
        onChange(emails.slice(0, -1));
      }
    },
    [text, commit, emails, onChange],
  );
  const onBlur = useCallback(() => {
    if (text.trim() && commit(text)) setText('');
  }, [text, commit]);
  const removeChip = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      const e = ev.currentTarget.dataset.email;
      onChange(emails.filter((x) => x !== e));
    },
    [emails, onChange],
  );

  const invalid = text.trim() !== '' && !EMAIL_RE.test(text.trim().toLowerCase());

  return (
    <div className={invalid ? 'bz-chips bz-chips-invalid' : 'bz-chips'}>
      {emails.map((e) => (
        <span key={e} className="bz-chip">
          {e}
          <button
            type="button"
            className="bz-chip-x"
            data-email={e}
            onClick={removeChip}
            aria-label={`Remove ${e}`}
          >
            <IconClose size={11} />
          </button>
        </span>
      ))}
      <input
        className="bz-chips-input"
        type="email"
        value={text}
        onChange={onInput}
        onKeyDown={onKey}
        onBlur={onBlur}
        placeholder={emails.length ? '' : (placeholder ?? 'name@example.com')}
        aria-label="Add attendee email"
      />
    </div>
  );
});
